import i18n from "i18next";
import { Trans } from "react-i18next";

import { getDateOneMonthFromNow } from "@/components/Questions/Date";
import { ActivityLevels } from "@/constants/activityLevel";
import { EventAnswersLabels } from "@/constants/answersLabels";
import {
    BodyInjures,
    FEMALE_BODY_INJURES_IMG,
    FEMALE_BODY_INJURES_IMG_SELECTED,
} from "@/constants/body-injures";
import {
    BodyMetricTypes,
    FemaleBodyFatRangeTypes,
    FemaleImagesByBodyFatRange,
    UnitTypes,
} from "@/constants/bodyMetrics";
import { EventScreenTypes } from "@/constants/event";
import {
    FitnessGoals,
    goalIngTextById,
    goalTextById,
    goalTextByIdChartImage,
} from "@/constants/fitnessGoal";
import { FitnessLevels } from "@/constants/fitnessLevels";
import { questionGroups } from "@/constants/questionGroups";
import { QUESTIONS_IDS, QuestionTypes } from "@/constants/questionsIds";
import bodyArms from "@/female/assets/body-injures/arms.webp";
import bodyBack from "@/female/assets/body-injures/back.webp";
import bodyElbows from "@/female/assets/body-injures/elbows.webp";
import bodyFeet from "@/female/assets/body-injures/feet.webp";
import bodyKnees from "@/female/assets/body-injures/knees.webp";
import bodyLegs from "@/female/assets/body-injures/legs.webp";
import bodyNeck from "@/female/assets/body-injures/neck.webp";
import bodyNoIssues from "@/female/assets/body-injures/no-issues.webp";
import bodyShoulders from "@/female/assets/body-injures/shoulders.webp";
import coreCalisthenics from "@/female/assets/calisthenics/core.webp";
import kneeCalisthenics from "@/female/assets/calisthenics/knee.webp";
import pressCalisthenics from "@/female/assets/calisthenics/press.webp";
import sidePlank from "@/female/assets/calisthenics/side-plank.webp";
import stretchCalisthenics from "@/female/assets/calisthenics/stretch.webp";
import femaleMuscleGain from "@/female/assets/fitness-goal/muscle-gain.webp";
import femaleWeightLoss from "@/female/assets/fitness-goal/weight-loss.webp";
import femaleLean from "@/female/assets/target-body-type/lean.webp";
import femaleMuscular from "@/female/assets/target-body-type/muscular.webp";
import femaleRipped from "@/female/assets/target-body-type/ripped.webp";
import {
    getCalisthenicsTextByFitnessLevel,
    getCalisthenicsTextByHealthRestrictions,
    getCalisthenicsTextByObstacle,
    getCalisthenicsTextByTrainingFrequency,
    getCalisthenicTextByPrimaryGoal,
} from "@/male/utils/calisthenics-texts";
import {
    BiggestDriveAnswers,
    BreathLoseAnswers,
    DailyWalkAnswers,
    DesiredBodyAnswers,
    DietTypeAnswers,
    EventAnswers,
    FeelBetweenMealsAnswers,
    FitnessExperienceAnswers,
    HealthProblemsAnswers,
    OtherSportsAnswers,
    PaceAnswers,
    PushUpsSquartsAnswers,
    SleepAmountAnswers,
    SportPreferAnswers,
    Train3MonthsAnswers,
    WaterConsumption,
    WeightLossGoal,
    YesNoAnswers,
} from "@/types/answers";
import { Genders } from "@/types/gender";
import { Answers } from "@/utils/AnswersProvider";

export const getQuestions = (prefix: string, answers: Answers) => [
    {
        id: QUESTIONS_IDS.PRIMARY_GOAL,
        event: `${prefix}_${QUESTIONS_IDS.PRIMARY_GOAL}`,
        type: QuestionTypes.RadioImageLeft,
        title: i18n.t("fitness_goal_prompt"),
        subtitle: i18n.t("goal_motivation_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: FitnessGoals.GainMuscle,
                text: goalTextById[FitnessGoals.GainMuscle],
                img: femaleMuscleGain,
            },
            {
                id: FitnessGoals.LoseWeight,
                text: goalTextById[FitnessGoals.LoseWeight],
                img: femaleWeightLoss,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CALISTHENICS_GOAL_FEEDBACK,
        type: QuestionTypes.CalisthenicsFeedbackLoops,
        groupTitle: questionGroups.Goals,
        event: `${prefix}_${QUESTIONS_IDS.CALISTHENICS_GOAL_FEEDBACK}`,
        img: sidePlank,
        title: getCalisthenicTextByPrimaryGoal[
            answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
        ]?.title,
        description:
            getCalisthenicTextByPrimaryGoal[answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals]
                ?.description,
    },
    {
        id: QUESTIONS_IDS.WEIGHT_LOSS_GOAL,
        event: `${prefix}_${QUESTIONS_IDS.WEIGHT_LOSS_GOAL}`,
        type: QuestionTypes.Radio,
        title: "What is your weight loss goal?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: WeightLossGoal.OneToTen,
                text:
                    answers.units === UnitTypes.Imperial
                        ? "Lose 2.2-22 lbs for good"
                        : "Lose 1-10 kg for good",
            },
            {
                id: WeightLossGoal.ElevenToTwenty,
                text:
                    answers.units === UnitTypes.Imperial
                        ? "Lose 23-44 lbs for good"
                        : "Lose 11-20 kg for good",
            },
            {
                id: WeightLossGoal.TwentyPlus,
                text:
                    answers.units === UnitTypes.Imperial
                        ? "Lose over 44 lbs for good"
                        : "Lose over 20 kg for good",
            },
            {
                id: WeightLossGoal.MaintainWeight,
                text: "Maintain weight and get fit",
            },
            {
                id: WeightLossGoal.NotDecided,
                text: "I haven't decided yet",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.FAMILIAR_CALISTHENICS,
        event: `${prefix}_${QUESTIONS_IDS.FAMILIAR_CALISTHENICS}`,
        type: QuestionTypes.Radio,
        title: "How familiar are you with calisthenics?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "starting",
                text: "I’m just starting",
            },
            {
                id: "some_experience",
                text: "I have some experience",
            },
            {
                id: "proficient",
                text: "I am proficient",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DESIRED_BODY,
        event: `${prefix}_${QUESTIONS_IDS.DESIRED_BODY}`,
        type: QuestionTypes.RadioImageLeft,
        title: i18n.t("target_body_prompt"),
        subtitle: i18n.t("target_body_motivation", {
            context: Genders.Female,
            defaultValue: "target_body_motivation",
        }),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: DesiredBodyAnswers.Lean,
                text: i18n.t("lean", {
                    context: Genders.Female,
                    defaultValue: "target_body_motivation",
                }),
                img: femaleLean,
            },
            {
                id: DesiredBodyAnswers.Muscular,
                text: i18n.t("toned"),
                img: femaleMuscular,
            },
            {
                id: DesiredBodyAnswers.Ripped,
                text: i18n.t("fit"),
                img: femaleRipped,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.BIGGEST_DRIVE,
        event: `${prefix}_${EventScreenTypes.BIGGEST_DRIVE}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("biggest_drive_prompt", {
            goal: goalIngTextById[answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals],
        }),
        subtitle: i18n.t("biggest_drive_motivation_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: BiggestDriveAnswers.Appearance,
                text: i18n.t("physical_appearance"),
            },
            {
                id: BiggestDriveAnswers.EnjoyingTime,
                text: i18n.t("enjoying_time"),
            },
            {
                id: BiggestDriveAnswers.GettingHealthier,
                text: i18n.t("getting_healthier"),
            },
            {
                id: BiggestDriveAnswers.FeelingHappier,
                text: i18n.t("feeling_happier"),
            },
            {
                id: BiggestDriveAnswers.Other,
                text: i18n.t("other"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PROMO_SOCIAL_PROOF,
        event: `${prefix}_${QUESTIONS_IDS.PROMO_SOCIAL_PROOF}`,
        type: QuestionTypes.PromoSocialProof,
    },
    {
        id: QUESTIONS_IDS.TARGET_AREAS,
        event: `${prefix}_${QUESTIONS_IDS.TARGET_AREAS}`,
        type: QuestionTypes.CheckboxInline,
        title: i18n.t("focus_areas_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: BodyInjures.FullBody,
                text: i18n.t("full_body"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.FullBody],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.FullBody],
            },
            {
                id: BodyInjures.Shoulders,
                text: i18n.t("shoulders"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Shoulders],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Shoulders],
            },
            {
                id: BodyInjures.Arms,
                text: i18n.t("arms"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Arms],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Arms],
            },
            {
                id: BodyInjures.Core,
                text: i18n.t("back"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Core],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Core],
            },
            {
                id: BodyInjures.Chest,
                text: i18n.t("chest"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Chest],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Chest],
            },
            {
                id: BodyInjures.Abs,
                text: i18n.t("belly"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Abs],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Abs],
            },
            {
                id: BodyInjures.Glutes,
                text: i18n.t("butt"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Glutes],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Glutes],
            },
            {
                id: BodyInjures.Legs,
                text: i18n.t("legs"),
                img: FEMALE_BODY_INJURES_IMG[BodyInjures.Legs],
                selectedImg: FEMALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Legs],
            },
        ],
    },
    {
        id: QUESTIONS_IDS.BODY_FAT,
        event: `${prefix}_${QUESTIONS_IDS.BODY_FAT}`,
        type: QuestionTypes.Range,
        groupTitle: questionGroups.Personal,
        title: i18n.t("body_fat_level_prompt"),
        answers: Object.values(FemaleBodyFatRangeTypes).map((type) => ({
            id: type,
            value: type,
            img: FemaleImagesByBodyFatRange[type],
        })),
    },
    {
        id: QUESTIONS_IDS.OBSTACLES,
        event: `${prefix}_${QUESTIONS_IDS.OBSTACLES}`,
        type: QuestionTypes.Radio,
        progressBarBreakPoint: true,
        title: (
            <Trans
                i18nKey="obstacle_prompt"
                values={{
                    goal: goalTextById[answers[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals],
                }}
            >
                <span className="text-white female:text-zing-warm-tangerine-500" />
            </Trans>
        ),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "consistencyLack",
                text: i18n.t("lack_of_consistency"),
            },
            {
                id: "timeConstraints",
                text: i18n.t("time_constraints"),
            },
            {
                id: "lackOfMotivation",
                text: i18n.t("lack_of_motivation"),
            },
            {
                id: "fatigue",
                text: i18n.t("fatigue"),
            },
            {
                id: "lackOfVisibleProgress",
                text: i18n.t("lack_of_visible_progress"),
            },
            {
                id: "plateausInProgress",
                text: i18n.t("plateaus"),
            },
            {
                id: "lackOfSupport",
                text: i18n.t("lack_of_support"),
            },
            {
                id: "injuriesOrPain",
                text: i18n.t("injuries_or_pain"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CALISTHENICS_OBSTACLE_FEEDBACK,
        type: QuestionTypes.CalisthenicsFeedbackLoops,
        groupTitle: questionGroups.Motivation,
        event: `${prefix}_${QUESTIONS_IDS.CALISTHENICS_OBSTACLE_FEEDBACK}`,
        img: kneeCalisthenics,
        ...getCalisthenicsTextByObstacle[
            answers?.[QUESTIONS_IDS.OBSTACLES] as keyof typeof getCalisthenicsTextByObstacle
        ],
    },
    {
        id: QUESTIONS_IDS.PROMO_ZING_AI,
        event: `${prefix}_${QUESTIONS_IDS.PROMO_ZING_AI}`,
        type: QuestionTypes.PromoZingAi,
        groupTitle: questionGroups.ZingAi,
    },
    {
        id: QUESTIONS_IDS.HEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.HEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.HEIGHT,
        title: i18n.t("height_prompt"),
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.CURRENT_WEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.CURRENT_WEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.WEIGHT,
        title: i18n.t("current_weight_prompt"),
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.TARGET_WEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.TARGET_WEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.WEIGHT,
        weightType: "target",
        title: i18n.t("target_weight_prompt"),
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.AGE,
        event: `${prefix}_${QUESTIONS_IDS.AGE}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.AGE,
        title: i18n.t("age_prompt"),
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.PACE,
        event: `${prefix}_${QUESTIONS_IDS.PACE}`,
        type: QuestionTypes.Radio,
        title: "Your Plan, Your Pace",
        subtitle: "You know what works best for you. Which pace would you prefer?",
        groupTitle: questionGroups.Personal,
        answers: [
            {
                id: PaceAnswers.Fast,
                text: "As fast as possible",
            },
            {
                id: PaceAnswers.Steady,
                text: "Slow and steady wins the race",
            },
            {
                id: PaceAnswers.BetweenTwo,
                text: "Somewhere in between the two",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.INITIAL_WEIGHT_PREDICT,
        event: `${prefix}_${QUESTIONS_IDS.INITIAL_WEIGHT_PREDICT}`,
        type: QuestionTypes.InitialWeightPredict,
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.FITNESS_LEVEL,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_LEVEL}`,
        type: QuestionTypes.Radio,
        title: i18n.t("fitness_level_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FitnessLevels.Beginner,
                text: i18n.t(FitnessLevels.Beginner),
                description: i18n.t("beginner_description", {
                    context: Genders.Female,
                    defaultValue: "beginner_description",
                }),
            },
            {
                id: FitnessLevels.Intermediate,
                text: i18n.t(FitnessLevels.Intermediate),
                description: i18n.t("intermediate_description"),
            },
            {
                id: FitnessLevels.Advanced,
                text: i18n.t(FitnessLevels.Advanced),
                description: i18n.t("advanced_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CALISTHENICS_LEVEL_FEEDBACK,
        type: QuestionTypes.CalisthenicsFeedbackLoops,
        groupTitle: questionGroups.Fitness,
        event: `${prefix}_${QUESTIONS_IDS.CALISTHENICS_LEVEL_FEEDBACK}`,
        img: coreCalisthenics,
        ...getCalisthenicsTextByFitnessLevel[
            answers?.[QUESTIONS_IDS.FITNESS_LEVEL] as FitnessLevels
        ],
    },
    {
        id: QUESTIONS_IDS.TRAIN_3_MONTHS,
        event: `${prefix}_${QUESTIONS_IDS.TRAIN_3_MONTHS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("train_3months_prompt"),
        subtitle: i18n.t("train_3months_motivation_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: Train3MonthsAnswers.HardlyEver,
                text: i18n.t("hardly_ever"),
            },
            {
                id: Train3MonthsAnswers.One2PerWeek,
                text: i18n.t("1_2_per_week"),
            },
            {
                id: Train3MonthsAnswers.Three4PerWeek,
                text: i18n.t("3_4_per_week"),
            },
            {
                id: Train3MonthsAnswers.Five6PerWeek,
                text: i18n.t("5_6_per_week"),
            },
            {
                id: Train3MonthsAnswers.Everyday,
                text: i18n.t("everyday"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CALISTHENICS_TRAINING_FREQUENCY_FEEDBACK,
        type: QuestionTypes.CalisthenicsFeedbackLoops,
        groupTitle: questionGroups.Fitness,
        event: `${prefix}_${QUESTIONS_IDS.CALISTHENICS_TRAINING_FREQUENCY_FEEDBACK}`,
        img: pressCalisthenics,
        ...getCalisthenicsTextByTrainingFrequency[
            answers?.[
                QUESTIONS_IDS.TRAIN_3_MONTHS
            ] as keyof typeof getCalisthenicsTextByTrainingFrequency
        ],
    },
    {
        id: QUESTIONS_IDS.FITNESS_EXPERIENCE,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_EXPERIENCE}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("fitness_experience_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FitnessExperienceAnswers.PersonalTrainers,
                text: i18n.t("personal_trainers"),
            },
            {
                id: FitnessExperienceAnswers.FitnessApps,
                text: i18n.t("fitness_apps"),
            },
            {
                id: FitnessExperienceAnswers.WorkoutVideos,
                text: i18n.t("workout_videos"),
            },
            {
                id: FitnessExperienceAnswers.SelfTaught,
                text: i18n.t("self_taught"),
            },
            {
                id: FitnessExperienceAnswers.None,
                text: i18n.t("none"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.HEALTH_PROBLEMS,
        event: `${prefix}_${QUESTIONS_IDS.HEALTH_PROBLEMS}`,
        type: QuestionTypes.CheckboxImageLeft,
        title: i18n.t("injuries_prompt"),
        subtitle: i18n.t("injuries_motivation_prompt"),
        groupTitle: questionGroups.Fitness,
        emptyAnswer: {
            id: HealthProblemsAnswers.None,
            text: i18n.t("no_issues"),
            img: bodyNoIssues,
        },
        answers: [
            {
                id: HealthProblemsAnswers.BackPain,
                text: i18n.t("back"),
                img: bodyBack,
            },
            {
                id: HealthProblemsAnswers.KneePain,
                text: i18n.t("knees"),
                img: bodyKnees,
            },
            {
                id: HealthProblemsAnswers.ShoulderPain,
                text: i18n.t("shoulders"),
                img: bodyShoulders,
            },
            {
                id: HealthProblemsAnswers.NeckPain,
                text: i18n.t("neck"),
                img: bodyNeck,
            },
            {
                id: HealthProblemsAnswers.ArmPain,
                text: i18n.t("arms"),
                img: bodyArms,
            },
            {
                id: HealthProblemsAnswers.ElbowPain,
                text: i18n.t("elbows"),
                img: bodyElbows,
            },
            {
                id: HealthProblemsAnswers.LegPain,
                text: i18n.t("legs"),
                img: bodyLegs,
            },
            {
                id: HealthProblemsAnswers.FootPain,
                text: i18n.t("feet"),
                img: bodyFeet,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CALISTHENICS_RESTRICTIONS_FEEDBACK,
        type: QuestionTypes.CalisthenicsFeedbackLoops,
        groupTitle: questionGroups.Fitness,
        event: `${prefix}_${QUESTIONS_IDS.CALISTHENICS_RESTRICTIONS_FEEDBACK}`,
        img: stretchCalisthenics,
        ...getCalisthenicsTextByHealthRestrictions(
            answers?.[QUESTIONS_IDS.HEALTH_PROBLEMS] as string[]
        ),
    },
    {
        id: QUESTIONS_IDS.BREATH_LOSE,
        event: `${prefix}_${QUESTIONS_IDS.BREATH_LOSE}`,
        type: QuestionTypes.Radio,
        title: "Do you lose your breath when climbing stairs?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: BreathLoseAnswers.CantCatchMyBreath,
                text: "I'm so tired I can't catch my breath",
            },
            {
                id: BreathLoseAnswers.OutOfBreath,
                text: "I'm a little out of breath, but I can talk",
            },
            {
                id: BreathLoseAnswers.FeelGoodAfterClimbing,
                text: "I feel good after climbing the stairs",
            },
            {
                id: BreathLoseAnswers.CanHandleSeveralFlights,
                text: "I can handle several flights of stairs",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SQUATS,
        event: `${prefix}_${QUESTIONS_IDS.SQUATS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("squats_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: PushUpsSquartsAnswers.Few,
                text: i18n.t("fewer_than_12"),
            },
            {
                id: PushUpsSquartsAnswers.Average,
                text: i18n.t("13_to_20"),
            },
            {
                id: PushUpsSquartsAnswers.Lot,
                text: i18n.t("more_than_20"),
            },
            {
                id: PushUpsSquartsAnswers.NoInfo,
                text: i18n.t("dont_know"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.OTHER_SPORTS,
        event: `${prefix}_${QUESTIONS_IDS.OTHER_SPORTS}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("other_activities_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: OtherSportsAnswers.No,
                text: i18n.t("no"),
            },
            {
                id: OtherSportsAnswers.Cardio,
                text: i18n.t("cardio"),
                description: i18n.t("cardio_types"),
            },
            {
                id: OtherSportsAnswers.Flexibility,
                text: i18n.t("flexibility"),
                description: i18n.t("flexibility_types"),
            },
            {
                id: OtherSportsAnswers.MartialArts,
                text: i18n.t("martial_arts"),
                description: i18n.t("martial_arts_types"),
            },
            {
                id: OtherSportsAnswers.GameSports,
                text: i18n.t("sports"),
                description: i18n.t("game_sports_types"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DAILY_WALK,
        event: `${prefix}_${QUESTIONS_IDS.DAILY_WALK}`,
        type: QuestionTypes.Radio,
        title: i18n.t("daily_walk_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: DailyWalkAnswers.Few,
                text: i18n.t("less_than_1_hour"),
            },
            {
                id: DailyWalkAnswers.Average,
                text: i18n.t("1_to_2_hours"),
            },
            {
                id: DailyWalkAnswers.Lot,
                text: i18n.t("more_than_2_hours"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.ACTIVITY_LEVEL,
        event: `${prefix}_${QUESTIONS_IDS.ACTIVITY_LEVEL}`,
        type: QuestionTypes.Radio,
        title: i18n.t("activity_level_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: ActivityLevels.Sedentary,
                text: i18n.t("sedentary"),
                description: i18n.t("minimal_activity"),
            },
            {
                id: ActivityLevels.ModerateExercise,
                text: i18n.t("moderately_active", {
                    context: Genders.Female,
                    defaultValue: i18n.t("moderately_active"),
                }),
                description: i18n.t("some_movement"),
            },
            {
                id: ActivityLevels.Athlete,
                text: i18n.t("very_active"),
                description: i18n.t("physically_demanding"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.FEEL_BETWEEN_MEALS,
        event: `${prefix}_${QUESTIONS_IDS.FEEL_BETWEEN_MEALS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("between_meals_feelings_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FeelBetweenMealsAnswers.Sleepy,
                text: i18n.t("sleepy_when_hungry"),
            },
            {
                id: FeelBetweenMealsAnswers.Tired,
                text: i18n.t("tired_after_eating"),
            },
            {
                id: FeelBetweenMealsAnswers.Energized,
                text: i18n.t("always_energized"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SLEEP_AMOUNT,
        event: `${prefix}_${QUESTIONS_IDS.SLEEP_AMOUNT}`,
        type: QuestionTypes.Radio,
        title: i18n.t("sleep_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: SleepAmountAnswers.LessThan5,
                text: i18n.t("fewer_than_5_hours"),
            },
            {
                id: SleepAmountAnswers.From5To6,
                text: i18n.t("5_to_6_hours"),
            },
            {
                id: SleepAmountAnswers.From7To8,
                text: i18n.t("7_to_8_hours"),
            },
            {
                id: SleepAmountAnswers.Over8,
                text: i18n.t("over_8_hours"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.WATER_CONSUMPTION,
        event: `${prefix}_${QUESTIONS_IDS.WATER_CONSUMPTION}`,
        type: QuestionTypes.Radio,
        title: i18n.t("water_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: WaterConsumption.NoWater,
                text: i18n.t("only_tea_coffee"),
            },
            {
                id: WaterConsumption.Low,
                text: i18n.t("fewer_than_2_glasses"),
            },
            {
                id: WaterConsumption.Average,
                text: i18n.t("2_to_6_glasses"),
            },
            {
                id: WaterConsumption.AboveAverage,
                text: i18n.t("7_to_10_glasses"),
            },
            {
                id: WaterConsumption.Lot,
                text: i18n.t("more_than_10_glasses"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DIET_TYPE,
        type: QuestionTypes.Radio,
        // TODO: review analytics event name
        event: `${prefix}_dietary_preferences`,
        title: i18n.t("dietary_preferences_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: DietTypeAnswers.NoDiet,
                text: i18n.t("none"),
            },
            {
                id: DietTypeAnswers.Diabetes,
                text: i18n.t("diabetes"),
            },
            {
                id: DietTypeAnswers.Vegan,
                text: i18n.t("vegetarian_vegan"),
            },
            {
                id: DietTypeAnswers.GlutenFree,
                text: i18n.t("gluten_free"),
            },
            {
                id: DietTypeAnswers.LactoseFree,
                text: i18n.t("lactose_free"),
            },
            {
                id: DietTypeAnswers.NutAllergy,
                text: i18n.t("nut_allergy"),
            },
        ],
    },

    {
        id: QUESTIONS_IDS.TOO_EXPENSIVE_FITNESS_PROGRAM_FEE,
        event: `${prefix}_${QUESTIONS_IDS.TOO_EXPENSIVE_FITNESS_PROGRAM_FEE}`,
        title: (
            <Trans i18nKey="too_expensive_fitness_program_fee_prompt">
                <span className="text-zing-warm-tangerine-500" />
            </Trans>
        ),
        subtitle: i18n.t("enter_amount"),
        groupTitle: questionGroups.Lifestyle,
        type: QuestionTypes.CurrencyInput,
    },
    {
        id: QUESTIONS_IDS.PLAN_FAILED,
        event: `${prefix}_${QUESTIONS_IDS.PLAN_FAILED}`,
        type: QuestionTypes.Radio,
        title: (
            <Trans
                i18nKey="fitness_plan_failure_prompt"
                values={{
                    goal: goalTextByIdChartImage[
                        answers[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
                    ],
                }}
            >
                <span className="text-white female:text-zing-warm-tangerine-500" />
            </Trans>
        ),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: YesNoAnswers.Yes,
                text: i18n.t("yes"),
            },
            {
                id: YesNoAnswers.No,
                text: i18n.t("no"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PROMO_HELP,
        event: `${prefix}_${QUESTIONS_IDS.PROMO_HELP}`,
        type: QuestionTypes.PromoHelp,
        progressBarBreakPoint: true,
    },
    {
        id: QUESTIONS_IDS.SPORT_PREFER,
        event: `${prefix}_${QUESTIONS_IDS.SPORT_PREFER}`,
        type: QuestionTypes.Radio,
        title: i18n.t("workout_preference_prompt"),
        groupTitle: questionGroups.WorkoutPreferences,
        answers: [
            {
                id: SportPreferAnswers.Morning,
                text: i18n.t("morning"),
            },
            {
                id: SportPreferAnswers.Afternoon,
                text: i18n.t("afternoon"),
            },
            {
                id: SportPreferAnswers.Evening,
                text: i18n.t("evening"),
            },
            {
                id: SportPreferAnswers.Different,
                text: i18n.t("different_times"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.BMI_RESULTS,
        event: `${prefix}_${QUESTIONS_IDS.BMI_RESULTS}`,
        type: QuestionTypes.BmiPromoFemale,
    },
    {
        id: QUESTIONS_IDS.EVENT_MOTIVATION,
        event: `${prefix}_${QUESTIONS_IDS.EVENT_MOTIVATION}`,
        type: QuestionTypes.Radio,
        title: (
            <Trans
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                i18nKey="important_event_prompt"
                values={{
                    goal: goalTextByIdChartImage[
                        answers[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
                    ],
                }}
                context={Genders.Female}
                defaultValue={i18n.t("important_event_prompt")}
            >
                <span className="text-white female:text-zing-warm-tangerine-500" />
            </Trans>
        ),
        subtitle: i18n.t("important_event_motivation"),
        groupTitle: questionGroups.Personal,
        answers: [
            {
                id: EventAnswers.Vacation,
                text: EventAnswersLabels.vacation,
            },
            {
                id: EventAnswers.Wedding,
                text: EventAnswersLabels.wedding,
            },
            {
                id: EventAnswers.Reunion,
                text: EventAnswersLabels.reunion,
            },
            {
                id: EventAnswers.Birthday,
                text: EventAnswersLabels.birthday,
            },
            {
                id: EventAnswers.None,
                text: EventAnswersLabels.none,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.EVENT_DATE,
        event: `${prefix}_${QUESTIONS_IDS.EVENT_DATE}`,
        type: QuestionTypes.Date,
        value: getDateOneMonthFromNow(),
        title:
            answers[QUESTIONS_IDS.EVENT_MOTIVATION] &&
            ["sports", "summer"].includes(answers[QUESTIONS_IDS.EVENT_MOTIVATION] as string)
                ? i18n.t("event_timing_prompt")
                : i18n.t("conditional_event_timing_prompt", {
                      event: EventAnswersLabels[
                          answers[QUESTIONS_IDS.EVENT_MOTIVATION] as EventAnswers
                      ]?.toLowerCase(),
                  }),
        minDate: new Date().toISOString().split("T")[0],
        groupTitle: questionGroups.Personal,
    },
];
